import { computed, nextTick } from 'vue'
import { freezeObject, getBooleanFromValue, isFunction, isObject } from '@/global/utils/utils'
import { useTableService } from '@/global/composables/table/useTableService'

const TAB_KEY = 'product-history'

export function useProductHistory (options) {
  const {
    tableGridContentComponent,
    additionalFiltersComponent,
    route,
    trans
  } = options || {}

  const { buildConfig } = useTableService()
  const productId = route?.params?.id

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: `products/${productId}/history`
    }
  }))

  // The serial number column starts hidden and is revealed from the response
  // meta (`use_serial_number`) through `updateHeaders`. Keeping it out of the
  // reactive state means the tab config below never has to be rebuilt, which
  // would remount the table and its filters component (and refetch the data).
  const headers = [
    {
      label: trans('fleet/documents/prl_cargo.sr_no'),
      key: 'serial_number',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true,
      hidden: true
    },
    {
      label: trans('fleet/documents/prl_cargo.date'),
      key: 'date',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('fleet/documents/prl_cargo.time'),
      key: 'time',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('road-maintenance/work_orders.status_changed_by'),
      key: 'updated_by',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory/product.from_location'),
      key: 'previous_geolocation',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('fleet/communication.location'),
      key: 'geolocation',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory.number_of_days_at_location'),
      key: 'days_on_location',
      style: {
        textAlign: 'center',
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('road-maintenance/inventory/product.quantity'),
      key: 'quantity',
      style: {
        textAlign: 'center',
        whiteSpace: 'nowrap'
      },
      always_visible: true
    },
    {
      label: trans('road-maintenance/excavation-service.status'),
      key: 'status',
      style: {
        whiteSpace: 'nowrap'
      },
      always_visible: true
    }
  ]

  const onDataLoad = (event) => {
    const { data, updateHeaders } = event || {}
    const { meta } = data || {}
    const useSerialNumber = getBooleanFromValue(meta?.use_serial_number)

    if (!useSerialNumber || !isFunction(updateHeaders)) {
      return
    }

    nextTick(() => {
      updateHeaders([
        {
          key: 'serial_number',
          hidden: false
        }
      ])
    })
  }

  const cellsConfig = freezeObject({
    days_on_location: {
      style: {
        textAlign: 'center'
      }
    },
    quantity: {
      style: {
        textAlign: 'center'
      }
    },
    status: {
      type: 'status',
      custom_display_value: (data) => {
        if (data && data.status && isObject(data.status)) {
          return {
            ...data.status,
            ...{
              description: data.status.description ? trans(data.status.description) : '-'
            }
          }
        }
      },
      displayValue: 'description',
      colorObjectKey: 'color',
      textColorKey: 'text',
      backgroundColorKey: 'background'
    }
  })

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'location',
        label: trans('fleet/communication.location'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: `products/${productId}/history/locations`
        }
      },
      {
        key: 'updated_by',
        label: trans('road-maintenance/work_orders.status_changed_by'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'base',
          route: 'users/autocomplete'
        }
      }
    ]
  }))

  return computed(() => ({
    title: trans('road-maintenance/inventory.item_history'),
    key: TAB_KEY,
    tabContent: [
      {
        component: tableGridContentComponent,
        ...buildConfig({
          fetchOnCreate: false,
          apiConfig: apiConfig.value,
          showBackButton: {
            uiViewName: 'TemporaryRegulationsProducts'
          },
          title: trans('road-maintenance/inventory.item_history'),
          customFiltersConfig: {
            component: additionalFiltersComponent
          },
          onDataReceivedWithParams: onDataLoad,
          tableConfig: {
            cellsConfig,
            headers,
            filtersConfig: filtersConfig.value
          }
        })
      }
    ]
  }))
}
